import axios from "axios";
import { supabase } from "./supabase";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, timeout: 30000 });

// Cache the latest access token so the request interceptor never has to await
// supabase.auth.getSession() (which can hang briefly while the auto-refresh
// worker is acquiring its internal lock with a stale refresh token).
let cachedToken = null;

// Allow callers (e.g. the login flow) to push a token synchronously so the
// very next API call has it, without racing onAuthStateChange.
export const setAuthToken = (token) => { cachedToken = token || null; };

// Iter 29 — Developer impersonation header. When a developer hits
// "IMPERSONATE" on an org, the FE sets this to the target org_id so
// every subsequent request runs as that org. Cleared on stop.
let impersonateOrgId = (typeof window !== "undefined") ? window.localStorage.getItem("dev_impersonate_org_id") : null;
export const setImpersonateOrg = (orgId) => {
  impersonateOrgId = orgId || null;
  try {
    if (orgId) window.localStorage.setItem("dev_impersonate_org_id", orgId);
    else window.localStorage.removeItem("dev_impersonate_org_id");
  } catch { /* ignore quota */ }
};
export const getImpersonateOrg = () => impersonateOrgId;

// Prime the cache from any stored session.
supabase.auth.getSession().then(({ data }) => {
  cachedToken = data?.session?.access_token || null;
}).catch(() => { /* ignore — no session yet */ });

// Keep the cache in sync with every auth event.
supabase.auth.onAuthStateChange((_event, session) => {
  cachedToken = session?.access_token || null;
});

// Attach Supabase access token to every request — never await Supabase here.
api.interceptors.request.use((config) => {
  if (cachedToken) {
    config.headers = config.headers || {};
    config.headers["Authorization"] = `Bearer ${cachedToken}`;
  }
  if (impersonateOrgId) {
    config.headers = config.headers || {};
    config.headers["X-Impersonate-Org-Id"] = impersonateOrgId;
  }
  return config;
});

// Server-side enforced inactivity logout: if the backend returns 401 with
// detail.code === "session_idle", clear the Supabase session and redirect
// to /login with a flash message.
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const detail = error?.response?.data?.detail;
    if (error?.response?.status === 401 && detail && typeof detail === "object" && detail.code === "session_idle") {
      try {
        const { supabase } = await import("./supabase");
        await supabase.auth.signOut();
      } catch { /* ignore */ }
      try { sessionStorage.setItem("idle_logout_msg", detail.message || "Session expired."); } catch { /* ignore */ }
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  },
);

export const formatErr = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Request failed";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return String(d);
};

export const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;
export const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }) : "—");
export const fmtDateTime = (s) => (s ? new Date(s).toLocaleString("en-US", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");
